/**
 * Section components for **Alert** entity-kind in the Entity Details panel
 * (REQ-UI-067). Renders the SAPIENT Alert section schema from ADR-029 /
 * ADR-032 §4.2, populated from ``GET /api/query/alert/{id}/details`` via
 * :func:`useAlertDetail` (PR5a inc 4; supersedes the inc 1 placeholder).
 *
 * Sections (mirrors the Event/Aggregator ``SectionShell`` + ``SectionRow``
 * pattern; a section shows an empty note when its optional nested data is
 * absent rather than disappearing — operator-visibility, CLAUDE.md §8.1):
 *
 *   1. ALERT — alert_id, type (label), status, priority (badge),
 *      ranking, confidence, description.
 *   2. LOCATION — the projected WGS84 location. Pushing to the map is the
 *      Alerts-panel row right-click → "Select on Map" (REQ-UI-096); no
 *      per-section button (removed 2026-06-09 as redundant).
 *   3. REGION — region_id (the detail carries no region *geometry*).
 *   4. ASSOCIATIONS — ``associated_detection[]`` (cross-pane-linked) +
 *      ``associated_file[]`` + ``additional_information`` free text.
 *   5. SOURCE — a cross-pane link to the ``producer_id`` platform +
 *      the raising ``aggregator_id``.
 *   6. DISMISSAL — the dismissal state (who/when/why when dismissed;
 *      "Active" when not) + a **Dismiss** button reusing the inc3 dismiss
 *      mutation, gated to operator/admin (``ALERT_DISMISSER_ROLES``).
 *
 * The selection bus carries only the ``alert_id``; the hook fetches the
 * rest. A ``404`` resolves to ``null`` (no alert with that id) — an empty
 * state, not an error.
 *
 * Section **visibility** and **expand/collapse** are read from
 * :func:`useSectionVisibility("alert")` per REQ-UI-092 / REQ-UI-064: a
 * section the operator hides in the gear menu is elided entirely
 * (ADR-024 §4), and each rendered shell self-wires its collapsed default
 * off the :func:`EntityDetailsSectionsProvider`. (Realizes the
 * collapsible-section behavior once flagged for api-change-request #358,
 * now **superseded** by the v3 ``entity_details`` ``expanded`` model —
 * ADR-033 §10.)
 *
 * Cross-references:
 *   - REQ-UI-067 (Alert section schema) / REQ-UI-051 (SOURCE cross-pane
 *     select) / REQ-UI-062 (dismissal) / REQ-UI-014 (CDS provenance).
 *   - REQ-UI-092 (per-type visibility + expand/collapse defaults) /
 *     REQ-UI-064 (collapsible sections, all entity types).
 *   - REQ-QUERY-025 (Alert detail surface + dismissal).
 *   - ADR-029 §4 / §5 / §6.2 / ADR-032 §4.2 (Alert data model) /
 *     ADR-024 §4 (panel structure) / ADR-033 §10 (relocated section config).
 */
import { SectionShell, SectionRow, SectionPlaceholder } from "@/entity-details/section-shell";
import { useAlertDetail, type AlertDetail } from "@/entity-details/use-alert-detail";
import { useSectionVisibility } from "@/entity-details/use-section-visibility";
import { EntityDetailsSectionsProvider } from "@/entity-details/entity-details-sections-context";
import { recordTypeForEntityKind } from "@/entity-details/entity-record-mapping";
import { SourceBytesSection } from "@/query/source-bytes-section";
import { useInspectInList } from "@/selection/use-inspect-in-list";
import { useSession } from "@/auth/session-context";
import {
  ALERT_DISMISSER_ROLES,
  ALERT_DISPLAY_PLACEHOLDER,
  alertTypeLabel,
  describeAlertPriority,
  formatConfidence,
} from "@/alerts/alert-display";
import { useDismissAlert } from "@/alerts/use-dismiss-alert";
import { getStatusColor } from "@/map/marker-styling";
import { usePreferences } from "@/preferences/preferences-context";
import { useProducerNames } from "@/lib/use-producer-names";
import { shortId } from "@/lib/short-id";
import { formatCoordinate } from "@/units/format-coordinate";
import type { components } from "@/lib/api-types.gen";

type AlertAssociatedDetection = components["schemas"]["AlertAssociatedDetection"];
type AlertAssociatedFile = components["schemas"]["AlertAssociatedFile"];

/**
 * Operator-facing labels for the SAPIENT ``AlertStatus`` enum
 * (``schemas/proto/meridian/v1/data/alert.proto`` §AlertStatus):
 * 0 UNSPECIFIED / 1 ACTIVE / 2 ACKNOWLEDGE / 3 REJECT / 4 IGNORE /
 * 5 CLEAR. Local to this section (the only consumer) rather than exported
 * from :file:`alerts/alert-display` — mirrors that module's own local
 * ``_ALERT_TYPE_LABELS`` convention. Unknown values fall back to
 * ``Status <n>`` (forward-compatibility, CLAUDE.md §8.3).
 */
const _ALERT_STATUS_LABELS: Readonly<Record<number, string>> = {
  0: "Unspecified",
  1: "Active",
  2: "Acknowledge",
  3: "Reject",
  4: "Ignore",
  5: "Clear",
};

function alertStatusLabel(status: number): string {
  return _ALERT_STATUS_LABELS[status] ?? `Status ${status}`;
}

export function AlertSections({ alertId }: { alertId: string }): React.ReactElement {
  const query = useAlertDetail({ alertId });
  // Two-slot routing (2026-06-09): the SOURCE / Associations **platform** links
  // *navigate* Entity Details, so they ``inspect`` (inspected slot only — leave
  // the map ring put). The map push is the Alerts-panel row right-click →
  // "Select on Map" (the single push-to-map affordance; REQ-UI-096). The links
  // additionally surface the Platforms list tab (``inspectInList``, 2026-06-12;
  // REQ-UI-051) so the synced highlight is visible without a manual tab switch.
  const inspectInList = useInspectInList();
  // ``producer_id → display_name`` join for the SOURCE platform row label
  // (REQ-UI-110 parity with the Alerts list rows). Falls back to the raw id
  // when unresolved; the click still selects the platform by id.
  const names = useProducerNames();
  const sections = useSectionVisibility("alert");
  const { isVisible } = sections;
  const alert = query.data ?? null;
  const loading = query.isLoading;
  const error = query.isError ? query.error.message : null;

  // One shared "why is this section empty" note across the sections when
  // there's no alert payload to render (mirrors EventSections).
  const stateNote = loading
    ? "Loading alert…"
    : error !== null
      ? `Could not load alert: ${error}`
      : alert === null
        ? "Alert not found — it may have aged out of the queryable window."
        : null;

  return (
    <EntityDetailsSectionsProvider value={{ entityType: "alert", sections }}>
      {isVisible("alert") && <AlertSection alert={alert} alertId={alertId} stateNote={stateNote} />}
      {isVisible("location") && <LocationSection alert={alert} stateNote={stateNote} />}
      {isVisible("region") && <RegionSection alert={alert} stateNote={stateNote} />}
      {isVisible("associations") && (
        <AssociationsSection
          alert={alert}
          stateNote={stateNote}
          onSelectPlatform={(producerId) => inspectInList({ kind: "platform", id: producerId })}
        />
      )}
      {isVisible("source") && (
        <SourceSection
          alert={alert}
          stateNote={stateNote}
          platformName={alert !== null ? names.nameFor(alert.producer_id) : null}
          onSelectPlatform={(producerId) => inspectInList({ kind: "platform", id: producerId })}
        />
      )}
      {isVisible("dismissal") && (
        <DismissalSection alert={alert} alertId={alertId} stateNote={stateNote} />
      )}
      {isVisible("source_bytes") && (
        <SourceBytesSection typeId={recordTypeForEntityKind("alert")} recordId={alertId} />
      )}
    </EntityDetailsSectionsProvider>
  );
}

// ---- Sections -----------------------------------------------------

interface SectionProps {
  alert: AlertDetail | null;
  stateNote: string | null;
}

/**
 * ALERT — the headline alert fields. Priority renders as the shared
 * coloured badge (same swatch as the Alerts panel rows, via
 * :func:`describeAlertPriority` + ``getStatusColor`` — REQ-UI-046, no
 * invented hex). Colour is not the sole signal: the label is always shown.
 */
function AlertSection({
  alert,
  alertId,
  stateNote,
}: SectionProps & { alertId: string }): React.ReactElement {
  return (
    <SectionShell id="alert" title="Alert">
      <SectionRow
        label="Alert ID"
        value={
          // Short, glanceable handle (ALT-…) as the primary value; the full
          // ULID stays on hover/title + selectable so it's still
          // log-cross-referenceable (REQ-UI-067; CLAUDE.md §8.1).
          <code className="text-body-sm text-text-primary" title={alertId}>
            {shortId(alertId, "ALT")}
          </code>
        }
      />
      {stateNote !== null && <SectionPlaceholder note={stateNote} />}
      {alert !== null && (
        <>
          <SectionRow label="Type" value={alertTypeLabel(alert.alert_type)} />
          <SectionRow label="Status" value={alertStatusLabel(alert.status)} />
          <SectionRow label="Priority" value={<PriorityBadge priority={alert.priority} />} />
          <SectionRow label="Ranking" value={formatRatio(alert.ranking)} />
          <SectionRow label="Confidence" value={formatConfidence(alert.confidence)} />
          <SectionRow label="Description" value={alert.description ?? "—"} />
        </>
      )}
    </SectionShell>
  );
}

/** The shared priority badge — coloured pill + text label (REQ-UI-046). */
function PriorityBadge({ priority }: { priority: number }): React.ReactElement {
  const descriptor = describeAlertPriority(priority);
  const color = getStatusColor(descriptor.markerStatus);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-body-sm font-medium"
      style={{
        color,
        backgroundColor: `color-mix(in srgb, ${color} 16%, transparent)`,
      }}
      data-testid="alert-priority-badge"
    >
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {descriptor.label}
    </span>
  );
}

/**
 * LOCATION — the projected WGS84 location. When the location is null (an
 * unprojectable BSI range/bearing, REQ-INGEST-044) the coordinate rows are
 * replaced by an explanatory note.
 *
 * Pushing the alert to the map is the Alerts-panel row right-click →
 * "Select on Map" (REQ-UI-096), the single push-to-map affordance — so
 * there is no per-section "Show on map" button here (removed 2026-06-09 as
 * redundant with the row menu).
 */
function LocationSection({ alert, stateNote }: SectionProps): React.ReactElement {
  // Render the projected location in the operator's preferred coordinate
  // format (REQ-UI-084): one consolidated "Coordinates" row.
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  // Bind the projected lat/lon as a non-null pair (or null when either axis
  // is absent) so the render below narrows cleanly without a cast.
  const position =
    alert !== null && alert.latitude_deg !== null && alert.longitude_deg !== null
      ? { lat: alert.latitude_deg, lon: alert.longitude_deg }
      : null;
  return (
    <SectionShell id="location" title="Location">
      {alert === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {alert !== null && (
        <>
          {position !== null ? (
            <>
              <SectionRow
                label="Coordinates"
                value={formatCoordinate(position.lat, position.lon, format)}
              />
              <SectionRow label="Elevation" value={formatElevation(alert.elevation_m)} />
            </>
          ) : (
            <SectionPlaceholder note="No projected location (range/bearing unprojectable)." />
          )}
        </>
      )}
    </SectionShell>
  );
}

/**
 * REGION — the BSI ``region_id``. The PR4 :class:`AlertDetail` projects
 * only the region id (not the region *geometry*); the geometry would feed
 * a PR5b map-region overlay. Notes when absent.
 */
function RegionSection({ alert, stateNote }: SectionProps): React.ReactElement {
  return (
    <SectionShell id="region" title="Region">
      {alert === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {alert !== null && alert.region_id === null && (
        <SectionPlaceholder note="No region associated." />
      )}
      {alert !== null && alert.region_id !== null && (
        <SectionRow
          label="Region ID"
          value={<code className="text-body-sm text-text-primary">{alert.region_id}</code>}
        />
      )}
    </SectionShell>
  );
}

/**
 * ASSOCIATIONS — cross-sensor correlated detections + file attachments +
 * free-text additional information. Each associated detection links to
 * that detection via the bus (its ``object_id`` is the cross-sensor key);
 * files show type + url and are NOT auto-fetched.
 */
function AssociationsSection({
  alert,
  stateNote,
  onSelectPlatform,
}: SectionProps & {
  onSelectPlatform: (producerId: string) => void;
}): React.ReactElement {
  const associated = alert?.associated_detection ?? [];
  const files = alert?.associated_file ?? [];
  // ``additional_information`` defaults to "" (empty) when absent.
  const additional = alert?.additional_information ?? "";
  const empty = associated.length === 0 && files.length === 0 && additional === "";
  return (
    <SectionShell id="associations" title="Associations">
      {alert === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {alert !== null && empty && (
        <SectionPlaceholder note="No associated detections, files, or notes." />
      )}
      {associated.map((assoc, idx) => (
        <AssociatedDetectionRow
          key={`${assoc.object_id}-${idx}`}
          assoc={assoc}
          onSelectPlatform={onSelectPlatform}
        />
      ))}
      {files.map((file, idx) => (
        <AssociatedFileRow key={`${file.url}-${idx}`} file={file} />
      ))}
      {additional !== "" && <SectionRow label="Additional info" value={additional} />}
    </SectionShell>
  );
}

function AssociatedDetectionRow({
  assoc,
  onSelectPlatform,
}: {
  assoc: AlertAssociatedDetection;
  onSelectPlatform: (producerId: string) => void;
}): React.ReactElement {
  return (
    <SectionRow
      label="Node"
      value={
        // The reporting node's short platform id (``node_id``), clickable to
        // select that platform via its canonical ``producer_id`` (REQ-UI-067).
        // The raw associated-detection ULID is no longer shown.
        <button
          type="button"
          className="font-mono text-body-sm text-meridian-action hover:underline"
          onClick={() => onSelectPlatform(assoc.producer_id)}
          data-testid="alert-associated-link"
        >
          {assoc.node_id}
        </button>
      }
    />
  );
}

function AssociatedFileRow({ file }: { file: AlertAssociatedFile }): React.ReactElement {
  return (
    <SectionRow
      label={file.type || "File"}
      value={
        <span className="break-all font-mono text-body-sm text-text-secondary">{file.url}</span>
      }
    />
  );
}

/**
 * SOURCE — the originating platform (cross-pane-linked) + the raising
 * aggregator. Mirrors the Event/Detection "DETECTED BY" cross-pane select.
 */
function SourceSection({
  alert,
  stateNote,
  platformName,
  onSelectPlatform,
}: SectionProps & {
  /**
   * The source platform's display name (REQ-UI-110), or ``null`` when there's
   * no alert to resolve. Falls back to the raw ``producer_id`` upstream when the
   * producers inventory hasn't resolved it.
   */
  platformName: string | null;
  onSelectPlatform: (producerId: string) => void;
}): React.ReactElement {
  return (
    <SectionShell id="source" title="Source">
      {alert === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {alert !== null && (
        <>
          <SectionRow
            label="Platform"
            value={
              // Show the platform DISPLAY NAME (REQ-UI-110), not the raw
              // producer UUID; the click still selects the platform by id.
              // ``title`` keeps the raw id reachable on hover.
              <button
                type="button"
                className="font-mono text-body-sm text-meridian-action hover:underline"
                onClick={() => onSelectPlatform(alert.producer_id)}
                data-testid="alert-source-link"
                title={alert.producer_id}
              >
                {platformName ?? alert.producer_id}
              </button>
            }
          />
          <SectionRow
            label="Aggregator"
            value={
              <code className="text-body-sm text-text-primary" title={alert.aggregator_id}>
                {shortId(alert.aggregator_id, "AGG")}
              </code>
            }
          />
        </>
      )}
    </SectionShell>
  );
}

/**
 * DISMISSAL — the dismissal lifecycle. When the alert is dismissed
 * (``dismissed_at`` set) it shows who/when/why instead of the button;
 * while active it shows "Active" + a **Dismiss** button gated to
 * operator/admin (``ALERT_DISMISSER_ROLES``; the BFF independently
 * enforces the same gate — fail-safe UX, CLAUDE.md §8.1).
 *
 * The button reuses the inc3 :func:`useDismissAlert` mutation. On success
 * the optimistic store removal + the cross-session ``alert-dismissed`` SSE
 * converge (idempotent), and the alert's ``/details`` re-fetch reflects the
 * dismissal columns. A 403 surfaces inline without retry (a role change
 * mid-session).
 */
function DismissalSection({
  alert,
  alertId,
  stateNote,
}: SectionProps & { alertId: string }): React.ReactElement {
  const session = useSession();
  const canDismiss = session.hasAnyRole(ALERT_DISMISSER_ROLES);
  const dismiss = useDismissAlert();
  const isDismissed = alert !== null && alert.dismissed_at !== null;

  return (
    <SectionShell id="dismissal" title="Dismissal">
      {alert === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {alert !== null && isDismissed && (
        <>
          <SectionRow label="State" value="Dismissed" />
          <SectionRow label="Dismissed at" value={formatTimestamp(alert.dismissed_at)} />
          <SectionRow
            label="Dismissed by"
            value={alert.dismissed_by ?? ALERT_DISPLAY_PLACEHOLDER}
          />
          {alert.dismissal_note !== null && alert.dismissal_note !== "" && (
            <SectionRow label="Note" value={alert.dismissal_note} />
          )}
        </>
      )}
      {alert !== null && !isDismissed && (
        <>
          <SectionRow label="State" value="Active" />
          {canDismiss && (
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate({ alertId })}
                data-testid="alert-dismiss-button"
                className="rounded border border-border px-2 py-1 text-body-sm text-meridian-action hover:bg-bg-elevated disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:bg-transparent"
              >
                {dismiss.isPending ? "Dismissing…" : "Dismiss"}
              </button>
              {dismiss.isError && (
                <span
                  role="alert"
                  className="text-body-sm text-destructive"
                  data-testid="alert-dismiss-error"
                >
                  {dismiss.error.status === 403 ? "Not permitted" : "Dismiss failed"}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
}

// ---- Formatters ---------------------------------------------------

function formatTimestamp(iso: string | null): string {
  if (iso === null) return ALERT_DISPLAY_PLACEHOLDER;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatElevation(elevationM: number | null): string {
  if (elevationM === null || !Number.isFinite(elevationM)) {
    return ALERT_DISPLAY_PLACEHOLDER;
  }
  return `${elevationM.toFixed(1)} m`;
}

/** Format a BSI ranking in [0, 1] as a whole-percent string; em-dash for
 * null (the source omitted it). Reuses the confidence convention. */
function formatRatio(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return ALERT_DISPLAY_PLACEHOLDER;
  return `${Math.round(value * 100)}%`;
}
